/**
 * CFO Board View — Phase 15 Stage 1
 * ====================================
 * Single-page board-meeting-ready CFO dashboard. Replaces the
 * ops-oriented Financial Workspace layout.
 *
 * Stage 1 (this commit): TimeLens + Compare toggles + Headline AI
 * + Performance Strip (7 KPIs with sparklines + traffic lights).
 *
 * Stages 2-6 add: Variance Bridge, Trend Grid, Forward View, Entity
 * Heatmap, Risk Strip, PDF/PPT Export, [Explain] commentary.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  LineChart, Line, ResponsiveContainer, YAxis, XAxis,
  BarChart, Bar, Cell, Tooltip, CartesianGrid, AreaChart, Area, Legend,
} from 'recharts';
import { API_V1, authFetch, formatAmount, formatAmountCompact } from '../constants';
import { useGlobalEntity } from '../hooks/useGlobalEntity';

const API = `${API_V1}/cfo-board`;

type Lens = 'month' | 'quarter' | 'ytd' | 'ltm';
type Compare = 'prior_period' | 'prior_year' | 'budget';

interface SparkPoint { ym: string; value: number }
interface Kpi {
  key: string;
  label: string;
  format: 'money' | 'pct' | 'days' | 'int';
  value: number;
  prior_value: number | null;
  delta_abs: number | null;
  delta_pct: number | null;
  direction: 'up' | 'down';
  traffic: 'green' | 'yellow' | 'red' | 'neutral';
  sparkline: SparkPoint[];
  compare_label: string;
  lens_label: string;
  source?: string;  // e.g. "treasury" or "gl_cumulative" for Cash KPI
  source_note?: string;
}
interface PeriodOption { ym: string; je_count: number; entities: number; last_posted: string | null }
interface Headline { text: string; source: string }
interface Snapshot {
  period: string;
  lens: Lens;
  compare: Compare;
  entity_code: string;
  current_window: { start: string; end: string };
  compare_window: { start: string; end: string } | null;
  kpis: Kpi[];
  headline: Headline;
}
interface BridgeBar { label: string; value: number; type: 'start' | 'end' | 'positive' | 'negative'; base: number; top: number; magnitude: number }
interface Bridge {
  lens: Lens; compare: Compare; period: string;
  prior_ni: number; current_ni: number;
  bars: BridgeBar[];
  drivers: { revenue_delta: number; cogs_delta: number; opex_delta: number; other_delta: number };
}
interface TrendGrid {
  period: string;
  anchor: string;
  series: {
    revenue: SparkPoint[];
    gross_margin_pct: SparkPoint[];
    opex: SparkPoint[];
    cash: SparkPoint[];
    working_capital: SparkPoint[];
  };
  ar_aging: { bucket: string; value: number }[];
}
interface CashForecastWeek {
  week: number;
  start_date: string;
  end_date: string;
  opening_balance: number;
  ar_inflows: number;
  ap_outflows: number;
  recurring_outflows: number;
  net_change: number;
  closing_balance: number;
  confidence_80?: { low: number; high: number };
  confidence_95?: { low: number; high: number };
}
interface Covenant {
  id: number;
  covenant_name: string;
  covenant_type: string;
  threshold: number;
  operator: string;
  debt_agreement: string | null;
  snapshot_date: string | null;
  calculated_value: number | null;
  headroom_pct: number | null;
  is_breached: boolean;
  status: 'ok' | 'warning' | 'breached';
}
interface ReforecastPeriod {
  period: string;
  revenue: { actual: number; baseline: number; delta_pct: number | null };
  net_income: { actual: number; baseline: number; delta_pct: number | null };
  cash: { actual: number; baseline: number; delta_pct: number | null };
}
interface Forward {
  period: string;
  cash_forecast: {
    current_balance: number;
    min_projected_balance: number;
    breach_week: number | null;
    forecast_method: string;
    history_days: number;
    weekly: CashForecastWeek[];
  };
  covenants: Covenant[];
  reforecast_accuracy: {
    method: string;
    note: string;
    periods: ReforecastPeriod[];
  };
}
interface HeatmapCol { key: string; label: string; format: 'money' | 'pct' | 'int'; direction: 'up' | 'down' }
interface HeatmapCell { key: string; value: number; prior_value: number | null; delta_pct: number | null; traffic: 'green' | 'yellow' | 'red' | 'neutral' }
interface HeatmapRow { entity_code: string; entity_name: string; functional_currency: string; cells: HeatmapCell[] }
interface Heatmap { period: string; lens: Lens; compare: Compare; columns: HeatmapCol[]; rows: HeatmapRow[] }
interface Risk { severity: 'warning' | 'critical'; category: string; label: string; detail: string }
interface RiskStrip { period: string; risks: Risk[] }
interface AnomalyFlag { severity: 'warning' | 'critical'; label: string; detail: string; section: string }
interface AnomalyResp { period: string; flags: AnomalyFlag[] }
interface Explanation { section: string; text: string; source: 'ai' | 'fallback' }
interface DrillLine { line_id: number; je_id: number; je_number: string; posting_date: string | null; entity_code: string | null; account_code: string; account_name: string; debit: number; credit: number; description: string | null; source_type: string | null; source_reference: string | null; je_type: string }
interface DrillAccountSummary { account_code: string; account_name: string; total_debit: number; total_credit: number; net: number }
interface DrillResponse { kpi: string; period: string; lens: string; window: { start: string; end: string }; row_count: number; top_accounts: DrillAccountSummary[]; lines: DrillLine[] }

const TRAFFIC_COLORS: Record<string, string> = {
  green: 'var(--positive)',
  yellow: 'var(--warning)',
  red: 'var(--negative)',
  neutral: 'var(--text-hint)',
};

const TRAFFIC_DOTS: Record<string, string> = {
  green: '🟢',
  yellow: '🟡',
  red: '🔴',
  neutral: '⚪',
};

const LENS_LABELS: Record<Lens, string> = {
  month: 'Month',
  quarter: 'Quarter',
  ytd: 'YTD',
  ltm: 'LTM',
};

const COMPARE_LABELS: Record<Compare, string> = {
  prior_period: 'vs Prior',
  prior_year: 'vs PY',
  budget: 'vs Budget',
};

function formatKpi(kpi: Kpi): string {
  if (kpi.format === 'money') return formatAmountCompact(kpi.value);
  if (kpi.format === 'pct') return `${kpi.value.toFixed(1)}%`;
  if (kpi.format === 'days') return `${Math.round(kpi.value)}d`;
  return new Intl.NumberFormat('en-US').format(Math.round(kpi.value));
}

function formatDelta(kpi: Kpi): string {
  if (kpi.delta_pct === null || kpi.delta_pct === undefined) return '—';
  const sign = kpi.delta_pct >= 0 ? '+' : '';
  return `${sign}${kpi.delta_pct.toFixed(1)}%`;
}

// Build period dropdown options: last 12 months + the next 3 (so the
// user can scrub through history easily). YYYY-MM strings.
function buildPeriodOptions(): string[] {
  const out: string[] = [];
  const now = new Date();
  for (let i = -12; i <= 3; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  }
  return out.reverse();
}

function periodLabel(ym: string): string {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(y, m - 1, 1);
  return d.toLocaleString('en-US', { month: 'long', year: 'numeric' });
}

export default function CfoBoardView() {
  const { entity } = useGlobalEntity();
  const periodOptions = useMemo(buildPeriodOptions, []);
  const defaultPeriod = useMemo(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  }, []);

  const [period, setPeriod] = useState<string>(defaultPeriod);
  const [lens, setLens] = useState<Lens>('ltm');
  const [compare, setCompare] = useState<Compare>('prior_year');
  const [periodCatalog, setPeriodCatalog] = useState<PeriodOption[]>([]);
  const [snapshot, setSnapshot] = useState<Snapshot | null>(null);
  const [bridge, setBridge] = useState<Bridge | null>(null);
  const [trends, setTrends] = useState<TrendGrid | null>(null);
  const [forward, setForward] = useState<Forward | null>(null);
  const [heatmap, setHeatmap] = useState<Heatmap | null>(null);
  const [risks, setRisks] = useState<RiskStrip | null>(null);
  const [anomalies, setAnomalies] = useState<AnomalyResp | null>(null);
  const [explain, setExplain] = useState<{ section: string; loading: boolean; data: Explanation | null } | null>(null);
  const [drill, setDrill] = useState<{ kpi_key: string; label: string; loading: boolean; data: DrillResponse | null } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ period, lens, compare });
      const trendParams = new URLSearchParams({ period });
      const fwdParams = new URLSearchParams({ period, weeks: '12' });
      const riskParams = new URLSearchParams({ period });
      if (entity && entity !== '*' && entity !== 'All') {
        params.set('entity_code', entity);
        trendParams.set('entity_code', entity);
        fwdParams.set('entity_code', entity);
        riskParams.set('entity_code', entity);
      }
      const heatmapParams = new URLSearchParams({ period, lens, compare });
      const anomParams = new URLSearchParams({ period });
      if (entity && entity !== '*' && entity !== 'All') anomParams.set('entity_code', entity);
      const [snapRes, bridgeRes, trendRes, fwdRes, hmRes, rsRes, anomRes] = await Promise.all([
        authFetch(`${API}/snapshot?${params.toString()}`),
        authFetch(`${API}/variance-bridge?${params.toString()}`),
        authFetch(`${API}/trend-grid?${trendParams.toString()}`),
        authFetch(`${API}/forward?${fwdParams.toString()}`),
        authFetch(`${API}/entity-heatmap?${heatmapParams.toString()}`),
        authFetch(`${API}/risk-strip?${riskParams.toString()}`),
        authFetch(`${API}/anomalies?${anomParams.toString()}`),
      ]);
      if (!snapRes.ok) {
        const e = await snapRes.json().catch(() => ({ detail: 'Failed' }));
        setError(e.detail || `HTTP ${snapRes.status}`);
        setSnapshot(null);
      } else {
        setSnapshot(await snapRes.json());
      }
      if (bridgeRes.ok) setBridge(await bridgeRes.json()); else setBridge(null);
      if (trendRes.ok) setTrends(await trendRes.json()); else setTrends(null);
      if (fwdRes.ok) setForward(await fwdRes.json()); else setForward(null);
      if (hmRes.ok) setHeatmap(await hmRes.json()); else setHeatmap(null);
      if (rsRes.ok) setRisks(await rsRes.json()); else setRisks(null);
      if (anomRes.ok) setAnomalies(await anomRes.json()); else setAnomalies(null);
    } catch (e: any) {
      setError(String(e?.message || e));
      setSnapshot(null);
    }
    setLoading(false);
  }, [period, lens, compare, entity]);

  useEffect(() => { load(); }, [load]);

  // Fetch period catalog once on mount — drives the period dropdown
  // with JE count + entity coverage per month, so the operator sees
  // which periods are data-rich vs sparse before picking.
  useEffect(() => {
    (async () => {
      try {
        const res = await authFetch(`${API}/periods?months_back=24`);
        if (res.ok) {
          const data = await res.json();
          setPeriodCatalog(data.months || []);
        }
      } catch { /* ignore — period picker falls back to static range */ }
    })();
  }, []);

  const requestDrill = useCallback(async (kpi_key: string, label: string) => {
    setDrill({ kpi_key, label, loading: true, data: null });
    try {
      const params = new URLSearchParams({ kpi_key, period, lens, limit: '100' });
      if (entity && entity !== '*' && entity !== 'All') params.set('entity_code', entity);
      const res = await authFetch(`${API}/drill-down?${params.toString()}`);
      if (res.ok) {
        const data: DrillResponse = await res.json();
        setDrill({ kpi_key, label, loading: false, data });
      } else {
        const e = await res.json().catch(() => ({ detail: 'Failed' }));
        setDrill({ kpi_key, label, loading: false, data: null });
        alert(`Drill-down failed: ${e.detail || res.status}`);
      }
    } catch (e: any) {
      setDrill({ kpi_key, label, loading: false, data: null });
      alert(`Drill-down failed: ${e?.message || e}`);
    }
  }, [period, lens, entity]);

  const requestExplain = useCallback(async (section: string, context: any) => {
    setExplain({ section, loading: true, data: null });
    try {
      const res = await authFetch(`${API}/explain`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ section, context }),
      });
      if (res.ok) {
        const data: Explanation = await res.json();
        setExplain({ section, loading: false, data });
      } else {
        setExplain({ section, loading: false, data: { section, text: 'Failed to load explanation.', source: 'fallback' } });
      }
    } catch (e: any) {
      setExplain({ section, loading: false, data: { section, text: `Failed: ${e?.message || e}`, source: 'fallback' } });
    }
  }, []);

  return (
    <div style={{ padding: '24px 28px', maxWidth: 1400, margin: '0 auto', color: 'var(--text-primary)' }}>
      {/* ── Header ────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 24, fontWeight: 700, letterSpacing: '-0.4px' }}>CFO Board View</div>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>
            Single-page board-meeting summary · {periodLabel(period)}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            style={selectStyle}
            aria-label="Period"
            title="Months with JE count — lower count means data-sparse"
          >
            {periodCatalog.length > 0
              ? periodCatalog.map(p => {
                  const sparse = p.je_count < 50;
                  const label = `${periodLabel(p.ym)}  ·  ${p.je_count} JEs  ·  ${p.entities} entities${sparse ? '  ⚠' : ''}`;
                  return <option key={p.ym} value={p.ym}>{label}</option>;
                })
              : periodOptions.map(p => <option key={p} value={p}>{periodLabel(p)}</option>)}
          </select>
          <ExportButton label="Export PDF" format="pdf" period={period} lens={lens} compare={compare} entity={entity} />
          <ExportButton label="Export PPTX" format="pptx" period={period} lens={lens} compare={compare} entity={entity} />
        </div>
      </div>

      {/* ── Lens + Compare toggles ────────────────────────────── */}
      <div style={{ display: 'flex', gap: 24, marginBottom: 18, padding: '12px 16px', background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)' }}>
        <ToggleGroup
          label="Lens"
          options={(Object.keys(LENS_LABELS) as Lens[]).map(k => ({ key: k, label: LENS_LABELS[k] }))}
          value={lens}
          onChange={(v) => setLens(v as Lens)}
        />
        <div style={{ width: 1, background: 'var(--border-subtle)' }} />
        <ToggleGroup
          label="Compare"
          options={(Object.keys(COMPARE_LABELS) as Compare[]).map(k => ({ key: k, label: COMPARE_LABELS[k] }))}
          value={compare}
          onChange={(v) => setCompare(v as Compare)}
        />
      </div>

      {/* ── Anomaly banner (Stage 6) ──────────────────────────── */}
      {anomalies && anomalies.flags.length > 0 && (
        <div style={{ marginBottom: 12, padding: '12px 16px', background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 6 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--warning)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
            ⚠ Data-quality flags — review before board export
          </div>
          {anomalies.flags.map((f, i) => (
            <div key={i} style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 4, paddingLeft: 10, borderLeft: `2px solid ${f.severity === 'critical' ? 'var(--negative)' : 'var(--warning)'}` }}>
              <strong style={{ color: f.severity === 'critical' ? 'var(--negative)' : 'var(--warning)' }}>{f.label}</strong> — {f.detail}
            </div>
          ))}
        </div>
      )}

      {/* ── Headline AI ───────────────────────────────────────── */}
      <div style={{ marginBottom: 18, padding: '18px 20px', background: 'linear-gradient(135deg, var(--bg-card), rgba(59,130,246,0.06))', borderRadius: 10, border: '1px solid var(--border-subtle)', borderLeft: '3px solid var(--accent)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--accent)' }}>📌 The One Thing</span>
          {snapshot?.headline?.source === 'ai' && (
            <span style={{ fontSize: 9, fontWeight: 600, padding: '2px 6px', borderRadius: 4, background: 'rgba(59,130,246,0.15)', color: 'var(--accent)' }}>AI</span>
          )}
        </div>
        <div style={{ fontSize: 15, fontWeight: 500, lineHeight: 1.5, color: 'var(--text-primary)' }}>
          {loading && !snapshot ? 'Computing snapshot…'
            : error ? <span style={{ color: 'var(--negative)' }}>Failed to load: {error}</span>
            : snapshot?.headline?.text || '—'}
        </div>
      </div>

      {/* ── Performance Strip ─────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 10 }}>
        {(snapshot?.kpis || Array.from({ length: 7 }, () => null)).map((kpi, i) => (
          <KpiCard
            key={kpi?.key || i}
            kpi={kpi}
            loading={loading && !kpi}
            onClick={kpi ? () => requestDrill(kpi.key, kpi.label) : undefined}
          />
        ))}
      </div>

      {/* ── Variance Bridge ────────────────────────────────────── */}
      <Section
        title="Variance Bridge — Net Income drivers"
        subtitle={bridge ? `${formatCompact(bridge.prior_ni)} → ${formatCompact(bridge.current_ni)} (${COMPARE_LABELS[compare]})` : 'Decomposes the NI change into revenue / COGS / OpEx / other contributions'}
        onExplain={bridge ? () => requestExplain('variance_bridge', bridge) : undefined}
      >
        {bridge && bridge.bars.length ? <VarianceBridge bridge={bridge} /> : <EmptyHint>Computing…</EmptyHint>}
      </Section>

      {/* ── Trend Grid ─────────────────────────────────────────── */}
      <Section
        title="Trends (LTM)"
        subtitle="12-month monthly time series"
        onExplain={trends ? () => requestExplain('trend', trends) : undefined}
      >
        {trends ? <TrendGridView trends={trends} /> : <EmptyHint>Loading…</EmptyHint>}
      </Section>

      {/* ── Forward View ───────────────────────────────────────── */}
      <Section
        title="Forward View"
        subtitle="Cash forecast · Covenant headroom · Reforecast accuracy"
        onExplain={forward ? () => requestExplain('forward', forward) : undefined}
      >
        {forward ? <ForwardViewPane forward={forward} /> : <EmptyHint>Loading forward view…</EmptyHint>}
      </Section>

      {/* ── Entity Heatmap + Risk Strip ────────────────────────── */}
      <Section
        title="Entity Heatmap & Risk Strip"
        subtitle="Per-entity performance grid + portfolio-level risks"
        onExplain={(heatmap || risks) ? () => requestExplain('heatmap_and_risk', { heatmap, risks }) : undefined}
      >
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10 }}>
          {heatmap ? <EntityHeatmapPane heatmap={heatmap} /> : <EmptyHint>Computing heatmap…</EmptyHint>}
          {risks ? <RiskStripPane risks={risks} /> : <EmptyHint>Scanning risks…</EmptyHint>}
        </div>
      </Section>

      {/* ── Drill-down modal ───────────────────────────────────── */}
      {drill && (
        <DrillModal drill={drill} onClose={() => setDrill(null)} />
      )}

      {/* ── Explain modal ──────────────────────────────────────── */}
      {explain && (
        <div
          role="presentation"
          onClick={() => setExplain(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 4000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
        >
          <div
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
            style={{ background: 'var(--bg-card)', borderRadius: 10, border: '1px solid var(--border-subtle)', padding: 24, maxWidth: 640, width: '100%', boxShadow: '0 10px 40px rgba(0,0,0,0.5)' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--accent)' }}>
                  ✨ Explain · {explain.section.replace(/_/g, ' ')}
                </span>
                {explain.data?.source === 'ai' && (
                  <span style={{ fontSize: 9, fontWeight: 600, padding: '2px 6px', borderRadius: 4, background: 'rgba(59,130,246,0.15)', color: 'var(--accent)' }}>AI</span>
                )}
              </div>
              <button
                type="button"
                onClick={() => setExplain(null)}
                style={{ background: 'transparent', border: 'none', color: 'var(--text-hint)', fontSize: 16, cursor: 'pointer' }}
              >
                ×
              </button>
            </div>
            <div style={{ fontSize: 14, lineHeight: 1.55, color: 'var(--text-primary)', whiteSpace: 'pre-wrap' }}>
              {explain.loading ? 'Thinking…' : explain.data?.text || 'No explanation available.'}
            </div>
          </div>
        </div>
      )}

      {/* ── Footer ────────────────────────────────────────────── */}
      <div style={{ marginTop: 24, fontSize: 11, color: 'var(--text-hint)', display: 'flex', justifyContent: 'space-between' }}>
        <span>
          Window: {snapshot?.current_window.start} → {snapshot?.current_window.end}
          {snapshot?.compare_window && (
            <> · Compare: {snapshot.compare_window.start} → {snapshot.compare_window.end}</>
          )}
        </span>
        <span>{snapshot?.entity_code === 'ALL' ? 'All entities' : `Entity ${snapshot?.entity_code}`}</span>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────

function ToggleGroup<T extends string>({
  label, options, value, onChange,
}: {
  label: string;
  options: { key: T; label: string }[];
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-hint)' }}>{label}</span>
      <div style={{ display: 'flex', gap: 2, background: 'rgba(255,255,255,0.03)', padding: 2, borderRadius: 6 }}>
        {options.map(o => (
          <button
            key={o.key}
            onClick={() => onChange(o.key)}
            style={{
              padding: '5px 12px',
              fontSize: 12,
              fontWeight: value === o.key ? 600 : 500,
              borderRadius: 4,
              border: 'none',
              cursor: 'pointer',
              background: value === o.key ? 'var(--accent)' : 'transparent',
              color: value === o.key ? '#fff' : 'var(--text-secondary)',
              transition: 'all 0.15s',
            }}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function KpiCard({ kpi, loading, onClick }: { kpi: Kpi | null; loading: boolean; onClick?: () => void }) {
  if (!kpi) {
    return (
      <div style={cardStyle}>
        <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-hint)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          {loading ? '…' : '—'}
        </div>
        <div style={{ fontSize: 22, fontWeight: 700, marginTop: 6 }}>—</div>
      </div>
    );
  }
  const trafficColor = TRAFFIC_COLORS[kpi.traffic];
  const interactive = !!onClick;
  return (
    <div
      {...(interactive ? { role: 'button', tabIndex: 0 } : {})}
      onClick={onClick}
      onKeyDown={onClick ? (e) => { if (e.key === 'Enter') onClick(); } : undefined}
      title={interactive ? `Click to drill into ${kpi.label}` : undefined}
      style={{ ...cardStyle, cursor: interactive ? 'pointer' : 'default' }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-hint)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          {kpi.label}
          {kpi.source === 'treasury' && (
            <span title={kpi.source_note || 'Treasury snapshot'} style={{ fontSize: 9, fontWeight: 600, marginLeft: 6, padding: '1px 5px', borderRadius: 3, background: 'rgba(34,197,94,0.15)', color: 'var(--positive)' }}>BANK</span>
          )}
          {kpi.source === 'gl_cumulative' && (
            <span title={kpi.source_note || 'GL cumulative — Treasury snapshots unavailable'} style={{ fontSize: 9, fontWeight: 600, marginLeft: 6, padding: '1px 5px', borderRadius: 3, background: 'rgba(245,158,11,0.15)', color: 'var(--warning)' }}>GL ⚠</span>
          )}
        </span>
        <span style={{ fontSize: 10 }}>{TRAFFIC_DOTS[kpi.traffic]}</span>
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, marginTop: 6, marginBottom: 2 }}>
        {formatKpi(kpi)}
      </div>
      <div style={{ height: 36, marginTop: 4 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={kpi.sparkline}>
            <YAxis hide domain={['dataMin', 'dataMax']} />
            <Line
              type="monotone"
              dataKey="value"
              stroke={trafficColor}
              strokeWidth={1.6}
              dot={false}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div style={{ fontSize: 11, fontWeight: 600, color: trafficColor, marginTop: 4 }}>
        {formatDelta(kpi)} <span style={{ fontWeight: 400, color: 'var(--text-hint)' }}>{kpi.compare_label}</span>
      </div>
      {kpi.prior_value !== null && kpi.prior_value !== undefined && (
        <div style={{ fontSize: 10, color: 'var(--text-hint)', marginTop: 2 }}>
          prior: {kpi.format === 'money' ? formatAmount(kpi.prior_value) : kpi.format === 'pct' ? `${kpi.prior_value.toFixed(1)}%` : Math.round(kpi.prior_value)}
        </div>
      )}
    </div>
  );
}

function Section({ title, subtitle, children, action, onExplain }: { title: string; subtitle?: string; children: React.ReactNode; action?: React.ReactNode; onExplain?: () => void }) {
  return (
    <div style={{ marginTop: 18, padding: '18px 20px', background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{title}</div>
          {subtitle && <div style={{ fontSize: 11, color: 'var(--text-hint)', marginTop: 4 }}>{subtitle}</div>}
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          {onExplain && (
            <button
              type="button"
              onClick={onExplain}
              style={{
                padding: '4px 10px',
                fontSize: 11,
                fontWeight: 600,
                border: '1px solid var(--accent)',
                background: 'transparent',
                color: 'var(--accent)',
                borderRadius: 4,
                cursor: 'pointer',
              }}
            >
              ✨ Explain
            </button>
          )}
          {action}
        </div>
      </div>
      {children}
    </div>
  );
}

function EmptyHint({ children }: { children: React.ReactNode }) {
  return <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-hint)', fontSize: 12 }}>{children}</div>;
}

function formatCompact(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  return formatAmountCompact(v);
}

function VarianceBridge({ bridge }: { bridge: Bridge }) {
  // Render: each bar drawn between (base, top). Recharts doesn't have a
  // first-class waterfall; we use a stacked BarChart where the lower
  // segment ("invisible") = base and the upper segment ("visible") =
  // magnitude. Color comes from the type field.
  const data = bridge.bars.map(b => ({
    label: b.label,
    invisible: b.base,
    visible: b.magnitude,
    type: b.type,
    signedValue: b.value,
  }));
  const colorFor = (t: string) => {
    if (t === 'start' || t === 'end') return 'var(--accent)';
    if (t === 'positive') return 'var(--positive)';
    return 'var(--negative)';
  };
  return (
    <div style={{ height: 260 }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} barSize={56} margin={{ top: 16, right: 16, left: 16, bottom: 4 }}>
          <CartesianGrid stroke="var(--border-subtle)" strokeDasharray="2 4" vertical={false} />
          <XAxis dataKey="label" tick={{ fill: 'var(--text-secondary)', fontSize: 11 }} axisLine={false} tickLine={false} />
          <YAxis tick={{ fill: 'var(--text-hint)', fontSize: 10 }} tickFormatter={(v) => formatAmountCompact(v)} axisLine={false} tickLine={false} width={56} />
          <Tooltip
            cursor={{ fill: 'rgba(255,255,255,0.04)' }}
            formatter={(_v: any, _n: any, ctx: any) => [formatAmount(ctx.payload.signedValue), ctx.payload.label]}
            labelFormatter={() => ''}
          />
          <Bar dataKey="invisible" stackId="w" fill="transparent" />
          <Bar dataKey="visible" stackId="w">
            {data.map((d, i) => <Cell key={i} fill={colorFor(d.type)} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function TrendChart({ title, data, color = 'var(--accent)', area = true, format = 'money' }: { title: string; data: SparkPoint[]; color?: string; area?: boolean; format?: 'money' | 'pct' }) {
  return (
    <div style={{ background: 'rgba(255,255,255,0.02)', borderRadius: 6, padding: 12 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-hint)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>{title}</div>
      <div style={{ height: 140 }}>
        <ResponsiveContainer width="100%" height="100%">
          {area ? (
            <AreaChart data={data} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="var(--border-subtle)" strokeDasharray="2 4" vertical={false} />
              <XAxis dataKey="ym" tick={{ fill: 'var(--text-hint)', fontSize: 9 }} axisLine={false} tickLine={false} interval={1} />
              <YAxis tick={{ fill: 'var(--text-hint)', fontSize: 9 }} tickFormatter={(v) => format === 'pct' ? `${v.toFixed(0)}%` : formatAmountCompact(v)} axisLine={false} tickLine={false} width={48} />
              <Tooltip formatter={(v: any) => format === 'pct' ? `${Number(v).toFixed(1)}%` : formatAmount(Number(v))} />
              <Area type="monotone" dataKey="value" stroke={color} fill={color} fillOpacity={0.15} strokeWidth={1.8} isAnimationActive={false} />
            </AreaChart>
          ) : (
            <LineChart data={data} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="var(--border-subtle)" strokeDasharray="2 4" vertical={false} />
              <XAxis dataKey="ym" tick={{ fill: 'var(--text-hint)', fontSize: 9 }} axisLine={false} tickLine={false} interval={1} />
              <YAxis tick={{ fill: 'var(--text-hint)', fontSize: 9 }} tickFormatter={(v) => format === 'pct' ? `${v.toFixed(0)}%` : formatAmountCompact(v)} axisLine={false} tickLine={false} width={48} />
              <Tooltip formatter={(v: any) => format === 'pct' ? `${Number(v).toFixed(1)}%` : formatAmount(Number(v))} />
              <Line type="monotone" dataKey="value" stroke={color} strokeWidth={1.8} dot={false} isAnimationActive={false} />
            </LineChart>
          )}
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function AgingChart({ data }: { data: { bucket: string; value: number }[] }) {
  return (
    <div style={{ background: 'rgba(255,255,255,0.02)', borderRadius: 6, padding: 12 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-hint)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>AR Aging (current)</div>
      <div style={{ height: 140 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
            <CartesianGrid stroke="var(--border-subtle)" strokeDasharray="2 4" vertical={false} />
            <XAxis dataKey="bucket" tick={{ fill: 'var(--text-hint)', fontSize: 10 }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fill: 'var(--text-hint)', fontSize: 9 }} tickFormatter={(v) => formatAmountCompact(v)} axisLine={false} tickLine={false} width={48} />
            <Tooltip formatter={(v: any) => formatAmount(Number(v))} />
            <Bar dataKey="value">
              {data.map((d, i) => (
                <Cell key={i} fill={d.bucket === 'Current' ? 'var(--positive)' : d.bucket === '90+' ? 'var(--negative)' : i >= 3 ? 'var(--warning)' : 'var(--accent)'} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function TrendGridView({ trends }: { trends: TrendGrid }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
      <TrendChart title="Revenue (monthly)" data={trends.series.revenue} color="var(--positive)" />
      <TrendChart title="Gross Margin %" data={trends.series.gross_margin_pct} color="var(--accent)" format="pct" area={false} />
      <TrendChart title="OpEx (monthly)" data={trends.series.opex} color="var(--warning)" />
      <TrendChart title="Cash (month-end)" data={trends.series.cash} color="var(--accent)" />
      <TrendChart title="Working Capital" data={trends.series.working_capital} color="#8b5cf6" />
      <AgingChart data={trends.ar_aging} />
    </div>
  );
}

function ForwardViewPane({ forward }: { forward: Forward }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 10 }}>
      <CashForecastPane data={forward.cash_forecast} />
      <CovenantHeadroomPane covenants={forward.covenants} />
      <ReforecastAccuracyPane data={forward.reforecast_accuracy} />
    </div>
  );
}

function CashForecastPane({ data }: { data: Forward['cash_forecast'] }) {
  // Build chart series: weekly closing + 80% confidence band
  const series = data.weekly.map(w => ({
    week: `W${w.week}`,
    closing: w.closing_balance,
    low80: w.confidence_80?.low,
    high80: w.confidence_80?.high,
  }));
  const breachWarn = data.breach_week !== null;
  return (
    <div style={{ background: 'rgba(255,255,255,0.02)', borderRadius: 6, padding: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-hint)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Cash Forecast (12wk)
        </span>
        <span style={{ fontSize: 10, color: 'var(--text-hint)' }}>
          method: {data.forecast_method?.toLowerCase()} · {data.history_days}d hist
        </span>
      </div>
      <div style={{ height: 180 }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={series} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid stroke="var(--border-subtle)" strokeDasharray="2 4" vertical={false} />
            <XAxis dataKey="week" tick={{ fill: 'var(--text-hint)', fontSize: 10 }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fill: 'var(--text-hint)', fontSize: 9 }} tickFormatter={(v) => formatAmountCompact(v)} axisLine={false} tickLine={false} width={56} />
            <Tooltip formatter={(v: any) => formatAmount(Number(v))} />
            <Area type="monotone" dataKey="high80" stroke="transparent" fill="var(--accent)" fillOpacity={0.08} />
            <Area type="monotone" dataKey="low80" stroke="transparent" fill="var(--bg-card)" fillOpacity={1} />
            <Line type="monotone" dataKey="closing" stroke="var(--accent)" strokeWidth={2} dot={false} isAnimationActive={false} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      <div style={{ display: 'flex', gap: 16, fontSize: 11, marginTop: 6, color: 'var(--text-secondary)' }}>
        <span>Today <strong style={{ color: 'var(--text-primary)' }}>{formatAmount(data.current_balance)}</strong></span>
        <span>Min projected <strong style={{ color: breachWarn ? 'var(--negative)' : 'var(--text-primary)' }}>{formatAmount(data.min_projected_balance)}</strong></span>
        {breachWarn && <span style={{ color: 'var(--negative)' }}>⚠ breach week {data.breach_week}</span>}
      </div>
    </div>
  );
}

function CovenantHeadroomPane({ covenants }: { covenants: Covenant[] }) {
  return (
    <div style={{ background: 'rgba(255,255,255,0.02)', borderRadius: 6, padding: 12 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-hint)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
        Covenant Headroom
      </div>
      {covenants.length === 0 ? (
        <div style={{ fontSize: 11, color: 'var(--text-hint)', padding: '12px 0' }}>No active covenants.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {covenants.map(c => {
            const headroom = c.headroom_pct ?? 0;
            const widthPct = Math.max(0, Math.min(100, headroom));
            const barColor = c.status === 'breached' ? 'var(--negative)' : c.status === 'warning' ? 'var(--warning)' : 'var(--positive)';
            return (
              <div key={c.id}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 3 }}>
                  <span style={{ fontWeight: 600 }}>{c.covenant_name}</span>
                  <span style={{ color: barColor, fontWeight: 600 }}>
                    {c.calculated_value !== null ? c.calculated_value.toFixed(2) : '—'} {c.operator} {c.threshold}
                  </span>
                </div>
                <div style={{ height: 6, background: 'rgba(255,255,255,0.06)', borderRadius: 3, overflow: 'hidden' }}>
                  <div style={{ width: `${widthPct}%`, height: '100%', background: barColor, transition: 'width 0.3s' }} />
                </div>
                <div style={{ fontSize: 9, color: 'var(--text-hint)', marginTop: 2 }}>
                  {c.is_breached ? 'BREACHED' : `${headroom.toFixed(1)}% headroom`}
                  {c.snapshot_date && <> · {c.snapshot_date}</>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ReforecastAccuracyPane({ data }: { data: Forward['reforecast_accuracy'] }) {
  return (
    <div style={{ background: 'rgba(255,255,255,0.02)', borderRadius: 6, padding: 12 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-hint)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
        Reforecast Accuracy (3-mo naive)
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
        <thead>
          <tr style={{ color: 'var(--text-hint)' }}>
            <th style={{ textAlign: 'left', fontWeight: 500, padding: '2px 4px' }}>Period</th>
            <th style={{ textAlign: 'right', fontWeight: 500, padding: '2px 4px' }}>Rev</th>
            <th style={{ textAlign: 'right', fontWeight: 500, padding: '2px 4px' }}>NI</th>
            <th style={{ textAlign: 'right', fontWeight: 500, padding: '2px 4px' }}>Cash</th>
          </tr>
        </thead>
        <tbody>
          {data.periods.map(p => (
            <tr key={p.period} style={{ borderTop: '1px solid var(--border-subtle)' }}>
              <td style={{ padding: '6px 4px', fontWeight: 600 }}>{p.period}</td>
              <td style={{ padding: '6px 4px', textAlign: 'right', color: deltaColor(p.revenue.delta_pct) }}>{formatPct(p.revenue.delta_pct)}</td>
              <td style={{ padding: '6px 4px', textAlign: 'right', color: deltaColor(p.net_income.delta_pct) }}>{formatPct(p.net_income.delta_pct)}</td>
              <td style={{ padding: '6px 4px', textAlign: 'right', color: deltaColor(p.cash.delta_pct) }}>{formatPct(p.cash.delta_pct)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ fontSize: 9, color: 'var(--text-hint)', marginTop: 8, lineHeight: 1.4 }}>{data.note}</div>
    </div>
  );
}

function deltaColor(p: number | null): string {
  if (p === null || p === undefined) return 'var(--text-hint)';
  const mag = Math.abs(p);
  if (mag < 5) return 'var(--positive)';
  if (mag < 15) return 'var(--warning)';
  return 'var(--negative)';
}

function formatPct(p: number | null): string {
  if (p === null || p === undefined) return '—';
  const sign = p >= 0 ? '+' : '';
  return `${sign}${p.toFixed(1)}%`;
}

function EntityHeatmapPane({ heatmap }: { heatmap: Heatmap }) {
  const trafficBg: Record<string, string> = {
    green: 'rgba(34,197,94,0.18)',
    yellow: 'rgba(245,158,11,0.18)',
    red: 'rgba(239,68,68,0.18)',
    neutral: 'rgba(255,255,255,0.04)',
  };
  const cellFormat = (cell: HeatmapCell, col: HeatmapCol): string => {
    if (col.format === 'money') return formatAmountCompact(cell.value);
    if (col.format === 'pct') return `${cell.value.toFixed(1)}%`;
    return new Intl.NumberFormat('en-US').format(Math.round(cell.value));
  };
  return (
    <div style={{ background: 'rgba(255,255,255,0.02)', borderRadius: 6, padding: 12, overflow: 'auto' }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-hint)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
        Entity Heatmap ({heatmap.lens.toUpperCase()} {COMPARE_LABELS[heatmap.compare]})
      </div>
      <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '4px 2px', fontSize: 11 }}>
        <thead>
          <tr style={{ color: 'var(--text-hint)' }}>
            <th style={{ textAlign: 'left', fontWeight: 500, padding: '4px 6px' }}>Entity</th>
            <th style={{ textAlign: 'left', fontWeight: 500, padding: '4px 6px' }}>Ccy</th>
            {heatmap.columns.map(c => (
              <th key={c.key} style={{ textAlign: 'right', fontWeight: 500, padding: '4px 6px' }}>{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {heatmap.rows.map(r => (
            <tr key={r.entity_code}>
              <td style={{ padding: '6px 6px', fontWeight: 600 }}>
                {r.entity_code}
                <div style={{ fontSize: 9, color: 'var(--text-hint)', fontWeight: 400 }}>{r.entity_name}</div>
              </td>
              <td style={{ padding: '6px 6px', color: 'var(--text-secondary)' }}>{r.functional_currency}</td>
              {r.cells.map((cell, i) => (
                <td
                  key={i}
                  style={{
                    padding: '8px 10px',
                    textAlign: 'right',
                    borderRadius: 4,
                    background: trafficBg[cell.traffic],
                    fontWeight: 600,
                  }}
                  title={cell.delta_pct !== null ? `${cell.delta_pct.toFixed(1)}% ${heatmap.compare === 'prior_year' ? 'vs PY' : heatmap.compare === 'prior_period' ? 'vs Prior' : 'vs Budget'}` : 'no comparison'}
                >
                  <div>{cellFormat(cell, heatmap.columns[i])}</div>
                  {cell.delta_pct !== null && (
                    <div style={{ fontSize: 9, fontWeight: 500, color: TRAFFIC_COLORS[cell.traffic] }}>
                      {cell.delta_pct >= 0 ? '+' : ''}{cell.delta_pct.toFixed(1)}%
                    </div>
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ fontSize: 9, color: 'var(--text-hint)', marginTop: 8 }}>
        Cell color shows traffic light vs {COMPARE_LABELS[heatmap.compare]}. Hover for delta %.
      </div>
    </div>
  );
}

function RiskStripPane({ risks }: { risks: RiskStrip }) {
  const severityColor: Record<string, string> = {
    critical: 'var(--negative)',
    warning: 'var(--warning)',
  };
  return (
    <div style={{ background: 'rgba(255,255,255,0.02)', borderRadius: 6, padding: 12 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-hint)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
        Risk Strip
      </div>
      {risks.risks.length === 0 ? (
        <div style={{ fontSize: 11, color: 'var(--positive)', padding: '12px 0' }}>✓ No active risks detected.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {risks.risks.map((r, i) => (
            <div key={i} style={{ padding: 10, borderLeft: `3px solid ${severityColor[r.severity]}`, background: 'rgba(255,255,255,0.03)', borderRadius: 4 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 2 }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: severityColor[r.severity] }}>{r.label}</span>
                <span style={{ fontSize: 9, color: 'var(--text-hint)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{r.category}</span>
              </div>
              <div style={{ fontSize: 10, color: 'var(--text-secondary)' }}>{r.detail}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function DrillModal({
  drill, onClose,
}: {
  drill: { kpi_key: string; label: string; loading: boolean; data: DrillResponse | null };
  onClose: () => void;
}) {
  return (
    <div
      role="presentation"
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 4500, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 20px', overflowY: 'auto' }}
    >
      <div
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
        style={{ background: 'var(--bg-card)', borderRadius: 10, border: '1px solid var(--border-subtle)', padding: 24, maxWidth: 1100, width: '100%', boxShadow: '0 10px 40px rgba(0,0,0,0.5)' }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--accent)' }}>
              🔍 Drill into · {drill.label}
            </div>
            {drill.data && (
              <div style={{ fontSize: 11, color: 'var(--text-hint)', marginTop: 4 }}>
                Window {drill.data.window.start} → {drill.data.window.end} · {drill.data.row_count} lines (top by absolute amount)
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{ background: 'transparent', border: 'none', color: 'var(--text-hint)', fontSize: 18, cursor: 'pointer' }}
          >
            ×
          </button>
        </div>

        {drill.loading || !drill.data ? (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-hint)' }}>Pulling the underlying ledger…</div>
        ) : (
          <>
            {/* Top accounts summary */}
            {drill.data.top_accounts.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-hint)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Top accounts</div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ color: 'var(--text-hint)', borderBottom: '1px solid var(--border-subtle)' }}>
                      <th style={{ textAlign: 'left', fontWeight: 500, padding: '4px 6px' }}>Account</th>
                      <th style={{ textAlign: 'right', fontWeight: 500, padding: '4px 6px' }}>Debits</th>
                      <th style={{ textAlign: 'right', fontWeight: 500, padding: '4px 6px' }}>Credits</th>
                      <th style={{ textAlign: 'right', fontWeight: 500, padding: '4px 6px' }}>Net</th>
                    </tr>
                  </thead>
                  <tbody>
                    {drill.data.top_accounts.map(a => (
                      <tr key={a.account_code} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                        <td style={{ padding: '5px 6px' }}>
                          <span style={{ fontWeight: 600 }}>{a.account_code}</span>
                          <span style={{ color: 'var(--text-secondary)', marginLeft: 6 }}>{a.account_name}</span>
                        </td>
                        <td style={{ padding: '5px 6px', textAlign: 'right' }}>{formatAmount(a.total_debit)}</td>
                        <td style={{ padding: '5px 6px', textAlign: 'right' }}>{formatAmount(a.total_credit)}</td>
                        <td style={{ padding: '5px 6px', textAlign: 'right', fontWeight: 600, color: a.net >= 0 ? 'var(--positive)' : 'var(--negative)' }}>{formatAmount(a.net)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* JE lines */}
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-hint)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>JE lines</div>
            <div style={{ maxHeight: 480, overflowY: 'auto', border: '1px solid var(--border-subtle)', borderRadius: 6 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                <thead style={{ position: 'sticky', top: 0, background: 'var(--bg-card)' }}>
                  <tr style={{ color: 'var(--text-hint)', borderBottom: '1px solid var(--border-subtle)' }}>
                    <th style={{ textAlign: 'left', fontWeight: 500, padding: '6px 8px' }}>Date</th>
                    <th style={{ textAlign: 'left', fontWeight: 500, padding: '6px 8px' }}>JE</th>
                    <th style={{ textAlign: 'left', fontWeight: 500, padding: '6px 8px' }}>Entity</th>
                    <th style={{ textAlign: 'left', fontWeight: 500, padding: '6px 8px' }}>Account</th>
                    <th style={{ textAlign: 'right', fontWeight: 500, padding: '6px 8px' }}>Debit</th>
                    <th style={{ textAlign: 'right', fontWeight: 500, padding: '6px 8px' }}>Credit</th>
                    <th style={{ textAlign: 'left', fontWeight: 500, padding: '6px 8px' }}>Description</th>
                    <th style={{ textAlign: 'left', fontWeight: 500, padding: '6px 8px' }}>Source</th>
                  </tr>
                </thead>
                <tbody>
                  {drill.data.lines.map(l => (
                    <tr key={l.line_id} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                      <td style={{ padding: '5px 8px', color: 'var(--text-secondary)' }}>{l.posting_date || '—'}</td>
                      <td style={{ padding: '5px 8px' }}>
                        <a href={`/gl-workbench?je_id=${l.je_id}`} style={{ color: 'var(--accent)', fontWeight: 600, textDecoration: 'none' }} title="Open in GL Workbench">
                          {l.je_number}
                        </a>
                      </td>
                      <td style={{ padding: '5px 8px', color: 'var(--text-secondary)' }}>{l.entity_code || '—'}</td>
                      <td style={{ padding: '5px 8px' }}>
                        <span style={{ fontWeight: 600 }}>{l.account_code}</span>
                        <span style={{ color: 'var(--text-secondary)', marginLeft: 4 }}>{l.account_name}</span>
                      </td>
                      <td style={{ padding: '5px 8px', textAlign: 'right' }}>{l.debit ? formatAmount(l.debit) : ''}</td>
                      <td style={{ padding: '5px 8px', textAlign: 'right' }}>{l.credit ? formatAmount(l.credit) : ''}</td>
                      <td style={{ padding: '5px 8px', color: 'var(--text-secondary)' }}>{l.description || '—'}</td>
                      <td style={{ padding: '5px 8px', color: 'var(--text-hint)', fontSize: 10 }}>{l.source_type || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ fontSize: 10, color: 'var(--text-hint)', marginTop: 8 }}>
              JE number links to the GL Workbench for the full document view.
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ExportButton({
  label, format, period, lens, compare, entity,
}: {
  label: string; format: 'pdf' | 'pptx';
  period: string; lens: Lens; compare: Compare; entity: string | null;
}) {
  const [busy, setBusy] = useState(false);
  const handle = async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams({ period, lens, compare, format });
      if (entity && entity !== '*' && entity !== 'All') params.set('entity_code', entity);
      const res = await authFetch(`${API}/export?${params.toString()}`);
      if (!res.ok) {
        alert(`Export failed: HTTP ${res.status}`);
        setBusy(false);
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cfo-board-${period}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      alert(`Export failed: ${e?.message || e}`);
    }
    setBusy(false);
  };
  return (
    <button
      type="button"
      onClick={handle}
      disabled={busy}
      style={{
        padding: '6px 12px',
        fontSize: 12,
        fontWeight: 600,
        border: '1px solid var(--accent)',
        background: format === 'pdf' ? 'var(--accent)' : 'transparent',
        color: format === 'pdf' ? '#fff' : 'var(--accent)',
        borderRadius: 6,
        cursor: busy ? 'wait' : 'pointer',
        opacity: busy ? 0.6 : 1,
      }}
    >
      {busy ? '…' : `⤓ ${label}`}
    </button>
  );
}

function SectionPlaceholder({ title, note }: { title: string; note: string }) {
  return (
    <div style={{ marginTop: 18, padding: '18px 20px', background: 'var(--bg-card)', borderRadius: 8, border: '1px dashed var(--border-subtle)' }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)' }}>{title}</div>
      <div style={{ fontSize: 11, color: 'var(--text-hint)', marginTop: 4 }}>{note}</div>
    </div>
  );
}

const cardStyle: React.CSSProperties = {
  padding: '14px 16px',
  background: 'var(--bg-card)',
  borderRadius: 8,
  border: '1px solid var(--border-subtle)',
};

const selectStyle: React.CSSProperties = {
  padding: '6px 10px',
  background: 'var(--bg-card)',
  color: 'var(--text-primary)',
  border: '1px solid var(--border-subtle)',
  borderRadius: 6,
  fontSize: 13,
};
